import { useState, useEffect, useRef, useCallback, useImperativeHandle, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip } from '@mui/material'
import { toast } from 'react-toastify'
import { useNotes } from '@/hooks/useNotes'
import { useCustomViewNotes } from '@/hooks/useCustomViewNotes'
import { NoteConflictDialog } from './NoteConflictDialog'
import { extractApiErrorCode } from '@/utils/apiError'
import { useAppStore } from '@/store/appStore'
import type { ApiCustomViewNote, ContentType } from '@/types/api'

export type NoteScope = 'personal' | 'view'

export type NoteSaveStatus = 'idle' | 'saved' | 'error'

export interface NoteScopeState {
  dirty: boolean
  saving: boolean
  status: NoteSaveStatus
}

export interface NoteScopeEditorHandle {
  /** Writes the buffer if it has unsaved changes. Resolves true when nothing is left unsaved. */
  save: () => Promise<boolean>
  discard: () => void
  restore: (body: string) => void
  focus: () => void
}

interface NoteScopeEditorProps {
  scope: NoteScope
  contentType: ContentType
  entityId: number
  sharedViewId: number | null
  autosave: boolean
  /** Shown above the textarea when both scopes are on screen, so each pane says whose note it is. */
  showLabel: boolean
  hidden: boolean
  autoFocus: boolean
  onStateChange: (scope: NoteScope, state: NoteScopeState) => void
  onDeleted: (scope: NoteScope) => void
  ref?: Ref<NoteScopeEditorHandle>
}

const AUTOSAVE_DELAY_MS = 2500

/**
 * The 409 carries the row that won under `details.current`, so both versions can be shown at
 * once. Re-fetching instead would race the next writer all over again. Absent means the other
 * side deleted the note rather than editing it.
 */
function extractConflictNote(err: unknown): ApiCustomViewNote | null {
  const details = (err as { response?: { data?: { details?: { current?: ApiCustomViewNote } } } })
    ?.response?.data?.details
  return details?.current ?? null
}

/**
 * One scope's editor: its own buffer, save state, autosave timer and conflict handling. Kept
 * mounted while its scope is deselected (`hidden`), so toggling a pane away never drops unsaved
 * text and autosave keeps covering it.
 */
export function NoteScopeEditor({
  scope, contentType, entityId, sharedViewId, autosave, showLabel, hidden, autoFocus,
  onStateChange, onDeleted, ref,
}: NoteScopeEditorProps) {
  const { t } = useTranslation()
  const { getNotesFor, saveNote, deleteNote } = useNotes()
  const isOffline = useAppStore((s) => s.isOffline)
  const personalNote = getNotesFor(contentType, entityId)[0] ?? null

  const viewScope = scope === 'view'
  // No revalidation key here: the panel shell owns navigation-driven re-reads for this query.
  const viewNotes = useCustomViewNotes(sharedViewId, viewScope && sharedViewId !== null)
  const viewNote = viewScope ? viewNotes.getNoteFor(contentType, entityId) : null
  const note = viewScope ? viewNote : personalNote
  const readOnly = viewScope && !viewNotes.canEdit
  // Shared-view notes have no offline queue — they go straight to the API — so while
  // offline the only honest option is to block the write rather than lose it.
  const offlineBlocked = viewScope && isOffline && !readOnly
  const canCopyPersonal = viewScope && viewNotes.isOwner && personalNote !== null

  const [body, setBody] = useState(note?.body ?? '')
  const [savedBody, setSavedBody] = useState(note?.body ?? '')
  // The token for the copy `savedBody` came from, not whatever the cache now holds. Cancelling the
  // conflict leaves the buffer based on the note the user started from, and the query keeps moving
  // underneath it (the cancel itself re-reads), so a token read live would let the next save
  // succeed against the winner's row and quietly overwrite it.
  const [baseRowVersion, setBaseRowVersion] = useState<string | null>(viewNote?.rowVersion ?? null)
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<NoteSaveStatus>('idle')
  const [conflict, setConflict] = useState<{ mine: string; theirs: ApiCustomViewNote | null } | null>(null)
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isDirty = body !== savedBody

  // Read inside the sync effect below without making it a dependency. If `isDirty` were a dep,
  // the effect would re-run the instant a save clears it — at which point `note` is still the
  // pre-save copy, and the user's just-saved text would flash back to the old body.
  const dirtyRef = useRef(isDirty)
  useEffect(() => {
    dirtyRef.current = isDirty
  }, [isDirty])

  // Re-sync when the note being edited changes OR when it loads for the first time
  // (e.g. after useInitialSync writes fresh data from the API to IndexedDB).
  // The shared view is part of the identity of a view-scoped note: switching views changes which
  // note this is, so it must reset the buffer rather than be treated as new data for the same one.
  const syncKey = `${contentType}:${entityId}:${viewScope ? sharedViewId ?? 0 : 0}`
  const lastSyncKey = useRef(syncKey)
  useEffect(() => {
    const movedToAnotherNote = lastSyncKey.current !== syncKey
    lastSyncKey.current = syncKey

    // Fresh data for the note already on screen must never replace what the user has typed.
    // Moving to a different note does reset, and the confirmation guard is what makes sure the
    // user agreed to lose the buffer before that happens.
    if (!movedToAnotherNote && dirtyRef.current) return

    const b = note?.body ?? ''
    setBody(b)
    setSavedBody(b)
    setBaseRowVersion(viewNote?.rowVersion ?? null)
    setSaveStatus('idle')
  }, [syncKey, note?.id, note?.body, viewNote?.rowVersion])

  useEffect(() => {
    onStateChange(scope, { dirty: isDirty, saving, status: saveStatus })
  }, [scope, isDirty, saving, saveStatus, onStateChange])

  const doSave = useCallback(async (value: string, expectedRowVersion?: string | null): Promise<boolean> => {
    if (saving || offlineBlocked) return false
    setSaving(true)
    try {
      if (viewScope) {
        const saved = await viewNotes.save.mutateAsync({
          contentType,
          entityId,
          body: value,
          // What we believed the note was when this buffer was last in sync. Undefined on a
          // first write, which the API treats as an unconditional create.
          expectedRowVersion: expectedRowVersion !== undefined ? expectedRowVersion : baseRowVersion,
        })
        setBaseRowVersion(saved.rowVersion)
      } else {
        await saveNote({ id: note?.id, contentType, entityId, body: value })
      }
      setSavedBody(value)
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 2000)
      return true
    } catch (err) {
      // Someone else got there first. Not an error the user can act on by retrying — open the
      // conflict view instead, seeded with the copy the server sent back on the 409.
      if (extractApiErrorCode(err) === 'CustomViewNoteConflict') {
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
        setConflict({ mine: value, theirs: extractConflictNote(err) })
        setSaveStatus('idle')
        return false
      }
      setSaveStatus('error')
      return false
    } finally {
      setSaving(false)
    }
  }, [saving, offlineBlocked, note?.id, baseRowVersion, contentType, entityId, saveNote, viewScope]) // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave debounce
  useEffect(() => {
    if (!autosave || readOnly || offlineBlocked) return
    if (!isDirty) return
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(() => {
      doSave(body)
    }, AUTOSAVE_DELAY_MS)
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    }
  }, [body, autosave, isDirty, readOnly, offlineBlocked, doSave])

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isDirty) return true
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
      return doSave(body)
    },
    discard: () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
      setBody(savedBody)
      setSaveStatus('idle')
    },
    restore: (restored: string) => {
      setBody(restored)
      textareaRef.current?.focus()
    },
    focus: () => textareaRef.current?.focus(),
  }), [isDirty, body, savedBody, doSave])

  const handleBlur = () => {
    if (!autosave || readOnly || offlineBlocked) return
    if (!isDirty) return
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    doSave(body)
  }

  const handleCancel = () => {
    setBody(savedBody)
    setSaveStatus('idle')
  }

  const handleDelete = async () => {
    if (!note) return
    setSaving(true)
    try {
      if (viewScope) {
        await viewNotes.remove.mutateAsync({ contentType, entityId, expectedRowVersion: baseRowVersion })
      } else {
        await deleteNote(note.id)
      }
      setBody('')
      setSavedBody('')
      setSaveStatus('idle')
    } finally {
      setSaving(false)
    }
    onDeleted(scope)
  }

  const handleCopyPersonalNote = async () => {
    try {
      const copied = await viewNotes.copyFromPersonal.mutateAsync({ contentType, entityId })
      setBody(copied.body)
      setSavedBody(copied.body)
      setBaseRowVersion(copied.rowVersion)
      setSaveStatus('saved')
      toast.success(t('notes.copiedToView'))
    } catch {
      toast.error(t('notes.copyToViewFailed'))
    }
  }

  // The resolved text is saved against the token of the copy that WON, so a third editor arriving
  // while the dialog was open is caught by the same check rather than silently overwritten.
  const handleConflictResolved = async (resolvedBody: string, rowVersion: string | null) => {
    setBody(resolvedBody)
    const ok = await doSave(resolvedBody, rowVersion)
    if (ok) setConflict(null)
  }

  // Their copy is already the stored one, so adopting it is a local reset: the buffer, its base and
  // its token all move to the winning row and the editor is clean again with nothing written.
  const handleConflictTakeTheirs = (theirs: ApiCustomViewNote) => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    setBody(theirs.body)
    setSavedBody(theirs.body)
    setBaseRowVersion(theirs.rowVersion)
    setSaveStatus('idle')
    setConflict(null)
  }

  const handleConflictCancel = () => {
    setConflict(null)
    viewNotes.refetch()
  }

  // The manual save bar is forced back on when autosave is blocked offline, so the
  // disabled button and the reason for it are visible together.
  const showSaveActions = !readOnly && (!autosave || offlineBlocked)
  const showActions = showSaveActions || canCopyPersonal

  return (
    <div className={`note-pane note-pane--${scope}`} hidden={hidden}>
      {showLabel && (
        <div className="note-pane-label">
          {viewScope ? t('notes.scopeView') : t('notes.scopePersonal')}
          {saving
            ? <span className="note-panel-saving-indicator"> · {t('notes.saving')}</span>
            : saveStatus === 'error'
              ? <span className="note-panel-error-indicator"> · {t('notes.saveFailed')}</span>
              : isDirty
                ? <span className="note-panel-unsaved-indicator"> · {t('notes.unsavedChanges')}</span>
                : saveStatus === 'saved' && <span className="note-panel-saved-indicator"> · {t('notes.saved')}</span>}
        </div>
      )}

      <textarea
        ref={textareaRef}
        className="note-panel-textarea"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onBlur={handleBlur}
        placeholder={readOnly ? t('notes.emptyViewNote') : t('notes.placeholder')}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-label={viewScope ? t('notes.scopeView') : t('notes.scopePersonal')}
      />

      {showActions && (
        <div className="note-panel-actions">
          {showSaveActions && (
            <>
              <Button
                className="note-panel-save"
                variant="contained"
                onClick={() => doSave(body)}
                disabled={saving || !isDirty || !body.trim() || offlineBlocked}
              >
                {saving ? t('notes.saving') : t('notes.save')}
              </Button>
              <Button
                className="note-panel-cancel"
                variant="text"
                onClick={handleCancel}
                disabled={!isDirty}
              >
                {t('notes.cancel')}
              </Button>
              {note && (
                <Button
                  className="note-panel-delete"
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={saving || offlineBlocked}
                >
                  {t('notes.delete')}
                </Button>
              )}
            </>
          )}
          {canCopyPersonal && (
            <Tooltip title={t('notes.copyPersonalToViewHint')}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyPersonalNote}
                  disabled={saving || viewNotes.copyFromPersonal.isPending || offlineBlocked}
                >
                  {t('notes.copyPersonalToView')}
                </Button>
              </span>
            </Tooltip>
          )}
          {offlineBlocked && (
            <span className="note-panel-offline-hint">{t('notes.viewScopeOffline')}</span>
          )}
        </div>
      )}

      {conflict && (
        // Keyed on the winning token: another editor landing a change while this is open
        // remounts the dialog with their text rather than leaving a stale copy on screen.
        <NoteConflictDialog
          key={conflict.theirs?.rowVersion ?? 'deleted'}
          mine={conflict.mine}
          theirs={conflict.theirs}
          saving={saving}
          onCancel={handleConflictCancel}
          onResolve={handleConflictResolved}
          onTakeTheirs={handleConflictTakeTheirs}
        />
      )}
    </div>
  )
}
